import PouchDB from 'pouchdb-browser';
import PouchDBFind from 'pouchdb-find';
import PouchDBUpsert from 'pouchdb-upsert';

PouchDB.plugin(PouchDBFind);
PouchDB.plugin(PouchDBUpsert);

export class LocalDb {
  private db: PouchDB.Database & { upsert: (docId: string, diffFunc: (doc: any) => any) => Promise<{ rev: string; updated: boolean; id: string }> };

  constructor(name: string) {
    this.db = new PouchDB(name) as any;
  }

  async insertDoc(doc: any): Promise<{ rev: string; updated: boolean; id: string }> {
    try {
      const response = await this.db.upsert(doc._id, existingDoc => {
        return { ...existingDoc, ...doc };
      });
      return response;
    } catch (error) {
      console.error('Error inserting document:', error);
      throw error;
    }
  }

  async getDoc(id: string): Promise<any> {
    try {
      const doc = await this.db.get(id);
      return doc;
    } catch (error) {
      if (error.name === 'not_found') {
        return null;
      }
      console.error('Error getting document:', error);
      throw error;
    }
  }

  async getAllDocs<T = any>(): Promise<T[]> {
    try {
      const result = await this.db.allDocs({ include_docs: true });
      return result.rows.filter(row => row.doc && !row.id.startsWith('_design')).map(row => row.doc as T);
    } catch (error) {
      console.error('Error getting all documents:', error);
      throw error;
    }
  }

  async removeDoc(id: string): Promise<void> {
    try {
      const doc = await this.getDoc(id);
      if (doc) {
        await this.db.remove(doc);
      }
    } catch (error) {
      console.error('Error removing document:', error);
      throw error;
    }
  }

  async getStringValue(key: string): Promise<string> {
    const doc = await this.getDoc(key);
    return doc?.value;
  }

  async setStringValue(key: string, value: string): Promise<void> {
    const doc = await this.getDoc(key);
    await this.insertDoc({ ...doc, _id: key, value });
  }

  async getBlobValue(key: string): Promise<Blob> {
    return await this.db.getAttachment(key, 'blob') as Blob;
  }

  async setBlobValue(key: string, value: Blob): Promise<void> {
    const doc = await this.db.get(key);
    await this.db.putAttachment(key, 'blob', doc._rev, value, 'application/octet-stream');
  }

  async putAttachment(docId: string, attachmentId: string, attachment: Blob, type: string): Promise<PouchDB.Core.Response> {
    try {
      const doc = await this.db.get(docId);
      const response = await this.db.putAttachment(docId, attachmentId, doc._rev, attachment, type);
      return response;
    } catch (error) {
      console.error('Error putting attachment:', error);
      throw error;
    }
  }

  async getAttachment(docId: string, attachmentId: string): Promise<Blob> {
    try {
      const attachment = await this.db.getAttachment(docId, attachmentId);
      return attachment as Blob;
    } catch (error) {
      return null;
    }
  }
}
